/**
 * Checking a material order against the estimate it belongs to.
 *
 * The estimate and its material order come out of one `generatePlan` run, so at
 * the moment of generation they agree. After that the order is edited — a line
 * added, a quantity rounded, a part swapped — and nothing said whether it still
 * matched what the customer was quoted. `job_id` ties the two documents to the
 * same job; it does not say their contents agree.
 *
 * The estimate's own lines cannot be diffed against the order's: the estimate
 * lists a package the customer buys, the order lists parts (ADR 0103). So the
 * expected parts are regenerated from what the estimate recorded at generation
 * — its template, the answers and the supplier (ADR 0156) — with the same two
 * calls the estimate builder makes, and the order is compared with that.
 *
 * Like `reconcile.ts`, which checks the order against the supplier's paperwork,
 * this does the comparison and says nothing about what to do. A signed change
 * order, a supplier out of stock, a rep who knows the roof better than the
 * report: an order is allowed to disagree with its quote. What is not allowed
 * is for nobody to notice.
 *
 * Nothing here can pass an estimate it cannot check. One built by hand, or
 * before the recipe was kept, comes back `cannot_compare` — never as a clean
 * result with no differences in it.
 */

import {
  generatePlan,
  type GeneratedLine,
  type GeneratorProduct,
  type RoofMeasurements,
  type RoofTemplateItem,
} from "./generator.js";
import {
  addCents,
  multiplyCents,
  subCents,
  sumBy,
  toCents,
  type Cents,
} from "./money.js";
import { squaresWithWaste } from "./autoExpenses.js";
import { normaliseDescription, type OrderedLine } from "./reconcile.js";
import { targetForCategory } from "./roofSlots.js";
import type { GenerationArea } from "./generationSnapshot.js";
import type { QuotePairing } from "./quoteMatch.js";
import type { PriceBook } from "./supplierPricing.js";
import {
  linesToPlan,
  type PlanProduct,
  type PlanTemplate,
  type TemplateAnswers,
} from "./templateLines.js";

/**
 * What a generating screen writes onto the estimate and onto each material
 * order it produces. One shape so the two screens cannot drift apart.
 */
export interface GenerationRecord {
  roof_template_id: string | null;
  generation_answers: TemplateAnswers | null;
}

export function generationRecord(
  templateId: string | null | undefined,
  answers: TemplateAnswers,
): GenerationRecord {
  return {
    roof_template_id: templateId || null,
    // Stored as-is: the read side hands it straight back to `linesToPlan`.
    generation_answers: templateId ? answers : null,
  };
}

/** Why an order could not be checked. Each is said, never passed. */
export type CannotCompareReason =
  /** Built by hand, or before the recipe was kept. */
  | "no_template"
  /** A template, but not the answers that decide which lines it produces. */
  | "no_answers"
  /** The order was generated from a different template than the estimate. */
  | "different_template";

/**
 * How the order differs from the quote.
 *
 * Named for this comparison rather than borrowed from `DiscrepancyKind`, whose
 * "missing from supplier" and "not ordered" mean something else on a receipt.
 */
export type QuoteDiscrepancyKind =
  /** On both, in a different quantity. */
  | "qty"
  /** On both, at a different unit price. */
  | "price"
  /** On the quote, not on the order. */
  | "not_ordered"
  /** On the order, not on the quote. */
  | "not_quoted"
  /**
   * One part ordered in place of the quoted one: HDZ ordered, Timberline NS
   * quoted; painted trim on a galvalume roof. Said as one row, because that is
   * one decision, rather than as a part missing and a part extra.
   */
  | "swapped";

export interface QuoteDiscrepancy {
  kind: QuoteDiscrepancyKind;
  description: string;
  productId: string | null;
  /**
   * The order line to correct. Null for pooled quantities across several lines
   * or parts not ordered at all; price differences name the individual line.
   */
  itemId: string | null;
  quotedQty: number | null;
  orderedQty: number | null;
  quotedPrice: Cents | null;
  orderedPrice: Cents | null;
  /**
   * The same quantities in squares of roof, for a product bought by area
   * (shingles, underlayment, membrane). Null for everything counted by the
   * piece or the foot. "Ordered 32 squares, sold 27" is how the difference is
   * read; 106 bundles against 90 is how it is bought.
   */
  quotedSquares: number | null;
  orderedSquares: number | null;
  /** For `swapped`: what was ordered instead. `description` is the quoted part. */
  orderedDescription?: string | null;
  orderedProductId?: string | null;
  /**
   * How much of `orderedQty` is on the estimate's other orders of the same
   * kind rather than this one. Absent when all of it is here.
   */
  orderedElsewhere?: number;
  /**
   * What the difference is worth on this order, in cents. Signed: positive
   * means the order costs more than the quote.
   */
  costImpact: Cents;
}

export type QuoteCheckResult =
  | {
      status: "cannot_compare";
      reason: CannotCompareReason;
      /** For `different_template`: the two ids, so the screen can name them. */
      estimateTemplateId?: string | null;
      orderTemplateId?: string | null;
    }
  | {
      status: "compared";
      /** The parts this order should carry, change orders included. */
      expected: GeneratedLine[];
      discrepancies: QuoteDiscrepancy[];
      matched: number;
      /**
       * The typed lines paired by Jev rather than by product or name, so the
       * screen can say which pairings a model proposed (ADR 0153).
       */
      proposedPairings: QuotePairing[];
      /**
       * Where the estimate sells something other than its template produced,
       * and the check followed the estimate (ADR 0154). Null when nothing
       * differed.
       */
      sold: SoldAdjustments | null;
      /**
       * The ids of the estimate's other orders this one was checked with
       * (`otherOrders` of the same kind), so the margin can swap all of them.
       */
      checkedWith: string[];
      quotedTotal: Cents;
      /** Across this order and the orders it was checked with. */
      orderedTotal: Cents;
      /** orderedTotal - quotedTotal. */
      netImpact: Cents;
    };

/** A catalogue product as the check reads it. */
export type QuoteCheckProduct = GeneratorProduct &
  PlanProduct & {
    /** Offered on the change-order picker (`products.change_order_preset`). */
    change_order_preset?: boolean | null;
  };

/** A change order as far as the material list is concerned. */
export interface QuoteChangeOrder {
  product_id?: string | null;
  description?: string | null;
  qty: unknown;
}

export interface QuoteCheckInput {
  estimate: {
    roof_template_id?: string | null;
    generation_answers?: TemplateAnswers | null;
    generation_supplier_id?: string | null;
  };
  order: {
    roof_template_id?: string | null;
    supplier_id?: string | null;
    /**
     * Where the order is in its life. Once it has been placed, what the
     * supplier charges is the receipt check's question, not the quote's: a
     * price that moved in the price book since then is not a difference
     * anybody can act on, so prices are compared only before placing.
     */
    status?: string | null;
    items: readonly OrderedLine[];
  };
  /** The estimate's template. Undefined when it has since been deleted. */
  template: PlanTemplate | undefined;
  templateItems: readonly RoofTemplateItem[];
  catalogue: readonly QuoteCheckProduct[];
  measurements: readonly RoofMeasurements[];
  prices: PriceBook;
  /** The estimate's change orders. Each one moves the baseline. */
  changeOrders?: readonly QuoteChangeOrder[];
  /**
   * Pairings proposed for typed lines, already filtered to the ones worth
   * using (`usablePairings` in quoteMatch.ts). Applied after product matching
   * and before name matching, and only to a line that carries no product.
   */
  pairings?: readonly QuotePairing[];
  /**
   * The estimate's own lines, as the customer was sold them. Its headline (the
   * priced line by the square) says which product and how many squares were
   * sold; where either differs from what the template produced, the expected
   * parts follow the estimate. Absent, the check follows the template alone.
   */
  estimateItems?: readonly EstimateLineSold[];
  /**
   * The estimate's other material orders. Those for the same supplier's parts
   * are checked together with this one, so a quote split across deliveries is
   * not reported as half missing on each.
   */
  otherOrders?: readonly QuoteSiblingOrder[];
  /**
   * The estimate's areas in another material, from its frozen copy
   * (`quoteSources`): a metal porch on a shingle house. Each is generated from
   * its own template on its own roof and bought from its own supplier, and
   * its parts are expected alongside the main package's (ADR 0166). An order
   * built from an area's template is checked, not refused as a different one.
   */
  areas?: readonly GenerationArea[] | undefined;
}

/** Another of the estimate's material orders, as the check reads it. */
export interface QuoteSiblingOrder {
  id?: string;
  supplier_id?: string | null;
  roof_template_id?: string | null;
  items: readonly OrderedLine[];
}

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * A name folded for matching: our filing prefix dropped, case and punctuation
 * gone. The same fold `reconcile.ts` uses, applied to the whole name rather
 * than word by word — both sides of this comparison are our own wording, so a
 * loose match would only hide a real difference.
 */
function foldName(value: string | null | undefined): string {
  const text = value ?? "";
  const bare = text.includes("|") ? text.slice(text.indexOf("|") + 1) : text;
  return normaliseDescription(bare);
}

/** Quantities are fractional (34.5 SQ); anything closer than this is equal. */
const QTY_TOLERANCE = 0.001;

/**
 * Which of the estimate's orders an order is: the main one (null), or the one
 * for a supplier a template line names.
 *
 * One estimate can raise several orders: a line that names its own supplier —
 * furring strips from Home Depot — goes on that supplier's order, and every
 * other line on the order for the supplier the estimate was generated against.
 * A named line whose supplier is the estimate's own goes on the main order,
 * because that is where generation put it.
 *
 * The supplier alone does not settle it. An order whose supplier was changed
 * after generation is still the main order, even when it was changed to the
 * named supplier — so an order for a named supplier that carries a part only
 * the main order has is the main order.
 */
function partitionOf(
  lines: readonly GeneratedLine[],
  order: { supplier_id?: string | null; items: readonly OrderedLine[] },
  generationSupplierId: string | null,
): string | null {
  const supplier = order.supplier_id ?? null;
  if (supplier === null || supplier === generationSupplierId) return null;
  if (!lines.some((line) => line.supplierId === supplier)) return null;

  const onSplit = new Set(
    lines.filter((l) => l.supplierId === supplier).map((l) => l.product_id),
  );
  const onMainOnly = new Set(
    lines
      .filter((l) => isMainLine(l, generationSupplierId))
      .map((l) => l.product_id)
      .filter((id) => !onSplit.has(id)),
  );
  const carriesMain = order.items.some(
    (item) => item.product_id && onMainOnly.has(item.product_id),
  );
  return carriesMain ? null : supplier;
}

const isMainLine = (
  line: GeneratedLine,
  generationSupplierId: string | null,
): boolean =>
  line.supplierId === null ||
  (generationSupplierId !== null && line.supplierId === generationSupplierId);

/**
 * The regenerated lines one of the estimate's orders should carry, one line per
 * part.
 *
 * The generator keeps a part bought from two places as two lines. Where both
 * places are this order's — a named supplier that is also the estimate's own —
 * they are one part on one order, and two lines would pair only the first.
 */
function linesForPartition(
  lines: readonly GeneratedLine[],
  partition: string | null,
  generationSupplierId: string | null,
): GeneratedLine[] {
  const merged = new Map<string, GeneratedLine>();
  for (const line of lines) {
    const belongs =
      partition === null
        ? isMainLine(line, generationSupplierId)
        : line.supplierId === partition;
    if (!belongs) continue;
    const seen = merged.get(line.product_id);
    if (seen) {
      seen.qty += line.qty;
      seen.calc = `${seen.calc} · ${line.calc}`;
    } else {
      merged.set(line.product_id, { ...line });
    }
  }
  return [...merged.values()];
}

/**
 * Add the change orders onto the expected parts.
 *
 * A signed change order is the customer agreeing to more roof, so ten extra
 * bundles on the order are not a discrepancy once there is a change order for
 * them. Matched by product, then by name. A change order for a part that
 * belongs on another of the estimate's orders is that order's; one for a
 * material the quote never had becomes an expected line on the main order only,
 * priced the way the generator would have priced it; one for labour, or for
 * nothing in the catalogue, has no part to add and is left alone.
 */
function withChangeOrders(
  expected: GeneratedLine[],
  changeOrders: readonly QuoteChangeOrder[],
  catalogue: readonly QuoteCheckProduct[],
  prices: PriceBook,
  supplierId: string | null,
  allLines: readonly GeneratedLine[],
  partition: string | null,
): GeneratedLine[] {
  const lines = expected.map((line) => ({ ...line }));
  const byId = new Map(catalogue.map((product) => [product.id, product]));

  const find = (
    among: readonly GeneratedLine[],
    productId: string | null,
    name: string,
  ) =>
    (productId && among.find((line) => line.product_id === productId)) ||
    (name !== "" &&
      among.find((line) => foldName(line.description) === name)) ||
    null;

  for (const change of changeOrders) {
    const qty = num(change.qty);
    if (qty === 0) continue;

    const productId = change.product_id ?? null;
    const name = foldName(change.description);
    const existing = find(lines, productId, name);
    if (existing) {
      existing.qty += qty;
      continue;
    }
    // On another of the estimate's orders: that order's check counts it.
    if (find(allLines, productId, name)) continue;
    if (partition !== null) continue;

    const product = productId ? byId.get(productId) : undefined;
    if (!product) continue;
    if (targetForCategory(product.category, product.kind) !== "material_order")
      continue;
    // A change-order preset is the customer's price for a piece of work
    // ("Remove and replace additional plywood/OSB"), filed as material but not
    // a part any supplier sells: it costs nothing to buy. Expecting it on the
    // order reported a $0 part as not ordered, and "Set to quoted" would have
    // put it on the supplier's order. The sheets and boards the work uses are
    // bought as themselves. A preset that is a real part, with a cost, is
    // still expected.
    const cost = prices.costOf(product, supplierId);
    if (product.change_order_preset && cost === 0) continue;
    lines.push({
      target: "material_order",
      product_id: product.id,
      description: [product.category, product.name].filter(Boolean).join(" | "),
      unit: product.unit,
      qty,
      unitPrice: cost,
      sort_order: Number.MAX_SAFE_INTEGER,
      supplierId: null,
      calc: "change order",
    });
  }
  return lines;
}

/** An estimate line, as far as what was sold is concerned. */
export interface EstimateLineSold {
  product_id?: string | null;
  qty: unknown;
  unit?: string | null;
  sort_order?: number | null;
  /** Wording lines (a section) are not sold by the square. */
  section?: string | null;
}

/** What the estimate sells that the template did not produce. */
export interface SoldAdjustments {
  /** The headline product: the template's (in the finish chosen), and the estimate's. */
  product: { templateProductId: string; soldProductId: string } | null;
  /** Squares with waste: as the roof measures, and as the estimate sells. */
  squares: { measured: number; sold: number } | null;
}

/** Squares, to the hundredth, as the headline line is written. */
const SQUARE_TOLERANCE = 0.01;

/**
 * The line the customer reads the price of: the first line sold by the square
 * outside a wording section. The builder writes exactly one
 * (`writeHeadlineLine`).
 *
 * It need not name a product. A template with no headline product — most
 * shingle templates, and every flat one — writes its headline with none, and
 * requiring one would leave the squares it sells unread on all of them. With
 * no product only the squares are followed; the product needs one to follow.
 */
function headlineOf(
  items: readonly EstimateLineSold[],
): EstimateLineSold | undefined {
  return [...items]
    .filter(
      (item) =>
        (item.unit ?? "").trim().toUpperCase() === "SQ" &&
        !(item.section ?? "").trim(),
    )
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
}

/** Squares to the hundredth, the way `writeHeadlineLine` writes them. */
const toHundredths = (squares: number): number => Number(squares.toFixed(2));

/** Keys on a roof that are an area in squares, and so scale with what was sold. */
const AREA_KEYS = [
  "squares",
  "steep_mid_squares",
  "steep_high_squares",
  "pitch_7_12",
  "pitch_8_12",
  "pitch_9_12",
  "pitch_10_12",
  "pitch_11_12",
  "pitch_12_12",
  "pitch_13_12",
  "pitch_14_12",
  "pitch_15_12",
  "pitch_16_12",
] as const;

/**
 * The roofs, resized so that their squares with waste are what was sold.
 *
 * The headline is written as the roofs' squares with waste
 * (`squaresWithWaste`), so a headline that says otherwise is a rep who sold
 * the customer a different roof: 27 squares against 32 measured. Every area on
 * every roof scales by the same factor; lengths (eave, ridge, valley) do not,
 * because selling fewer squares does not shorten an eave.
 */
function resizedToSold(
  roofs: readonly RoofMeasurements[],
  sold: number,
): { roofs: RoofMeasurements[]; measured: number } | null {
  const measured = squaresWithWaste(roofs);
  if (measured <= 0 || sold <= 0) return null;
  // Rounded as the headline was, or a roof measuring 1.495 squares reads as
  // 1.50 here against the 1.49 written, and as a different roof sold. The
  // epsilon is for the subtraction: 1.50 - 1.49 is 0.010000000000000009.
  if (Math.abs(toHundredths(measured) - sold) <= SQUARE_TOLERANCE + 1e-9) {
    return null;
  }
  const factor = sold / measured;
  return {
    measured: toHundredths(measured),
    roofs: roofs.map((roof) => {
      const next: Record<string, unknown> = { ...roof };
      for (const key of AREA_KEYS) {
        if (roof[key] != null) next[key] = num(roof[key]) * factor;
      }
      return next as RoofMeasurements;
    }),
  };
}

/** Measurement keys whose coverage is square feet, as generator.ts reads them. */
const SQUARE_FOOT_KEYS = ["TOTAL_PITCH_AREA", "TOTAL_FLAT_AREA"];

/**
 * How many squares of roof a quantity of a product covers, or null when the
 * product is not bought by area.
 *
 * The same reading of `measurement` the generator uses to order it
 * (`measurementForProduct`): a key ending _SQUARES means coverage is in
 * squares, a square-foot key means square feet. No waste is added: this is the
 * roof the material covers, so 32 squares ordered against 27 sold says how much
 * more was bought than the quote allowed.
 */
export function squaresCovered(
  product:
    { coverage?: unknown; measurement?: string | null | undefined } | undefined,
  qty: number | null,
): number | null {
  if (!product || qty === null) return null;
  const coverage = num(product.coverage);
  if (coverage <= 0) return null;
  const keys = (product.measurement ?? "")
    .split(",")
    .map((k) => k.trim().toUpperCase());
  const perSquare = keys.some((k) => k.endsWith("_SQUARES"));
  const perSquareFoot = keys.some((k) => SQUARE_FOOT_KEYS.includes(k));
  if (!perSquare && !perSquareFoot) return null;
  const squares = perSquare ? qty * coverage : (qty * coverage) / 100;
  return Math.round(squares * 100) / 100;
}

/**
 * Categories too broad to say two products are alternatives. Starter strip and
 * a delivery charge are both filed under Other; neither was ordered instead of
 * the other.
 */
const GENERIC_CATEGORIES = ["", "other", "misc", "miscellaneous"];

type SwapProduct = GeneratorProduct & { galvalume_twin_id?: string | null };

/**
 * What a product is bought against: an area, a length, or a count.
 *
 * Read off the same `measurement` keys the generator uses. Ice and water by the
 * eave and a synthetic underlayment by the square are both underlayment by the
 * roll, and neither is ordered instead of the other.
 */
function measuredBy(product: {
  measurement?: string | null | undefined;
}): string {
  const keys = (product.measurement ?? "")
    .split(",")
    .map((k) => k.trim().toUpperCase())
    .filter(Boolean);
  if (
    keys.some(
      (k) =>
        k.endsWith("_SQUARES") ||
        SQUARE_FOOT_KEYS.includes(k) ||
        k.includes("AREA"),
    )
  ) {
    return "area";
  }
  if (keys.some((k) => k.endsWith("_LENGTH"))) return "length";
  return "count";
}

/**
 * Whether an ordered product stands in for a quoted one.
 *
 * Twins first (the painted and galvalume versions of one part, ADR 0121), then
 * two products filed under the same specific category, sold in the same unit
 * and bought against the same measurement: two shingles by the bundle over the
 * roof's area, two underlayments by the roll over it. A part missing and
 * another genuinely extra in the same category are not a swap when one is
 * bought by the eave and the other by the square.
 */
function swapStrength(
  quoted: SwapProduct | undefined,
  ordered: SwapProduct | undefined,
): number {
  if (!quoted || !ordered || quoted.id === ordered.id) return 0;
  if (
    quoted.galvalume_twin_id === ordered.id ||
    ordered.galvalume_twin_id === quoted.id
  ) {
    return 2;
  }
  const category = (quoted.category ?? "").trim().toLowerCase();
  const sameCategory =
    !GENERIC_CATEGORIES.includes(category) &&
    category === (ordered.category ?? "").trim().toLowerCase();
  const sameUnit =
    (quoted.unit ?? "").trim().toUpperCase() ===
    (ordered.unit ?? "").trim().toUpperCase();
  const sameMeasure = measuredBy(quoted) === measuredBy(ordered);
  return sameCategory && sameUnit && sameMeasure ? 1 : 0;
}

/**
 * Fold a quoted part that was not ordered and an ordered part that was not
 * quoted into one `swapped` row, where one stands in for the other. Each part
 * joins at most one swap; twins pair before same-category products. Lines
 * typed with no product are left alone: that is Jev's question.
 */
function withSwaps(
  discrepancies: readonly QuoteDiscrepancy[],
  catalogue: readonly SwapProduct[],
): QuoteDiscrepancy[] {
  const byId = new Map(catalogue.map((p) => [p.id, p]));
  const missing = discrepancies.filter(
    (d) => d.kind === "not_ordered" && d.productId,
  );
  const extra = discrepancies.filter(
    (d) => d.kind === "not_quoted" && d.productId,
  );

  const candidates = missing
    .flatMap((m) =>
      extra.map((e) => ({
        m,
        e,
        strength: swapStrength(byId.get(m.productId!), byId.get(e.productId!)),
      })),
    )
    .filter((c) => c.strength > 0)
    .sort((a, b) => b.strength - a.strength);

  const used = new Set<QuoteDiscrepancy>();
  const swaps = new Map<QuoteDiscrepancy, QuoteDiscrepancy>();
  for (const { m, e } of candidates) {
    if (used.has(m) || used.has(e)) continue;
    used.add(m);
    used.add(e);
    swaps.set(m, {
      kind: "swapped",
      description: m.description,
      productId: m.productId,
      itemId: e.itemId,
      quotedQty: m.quotedQty,
      orderedQty: e.orderedQty,
      quotedPrice: m.quotedPrice,
      orderedPrice: e.orderedPrice,
      quotedSquares: m.quotedSquares,
      orderedSquares: e.orderedSquares,
      orderedDescription: e.description,
      orderedProductId: e.productId,
      // The two rows already say "take the quoted line away, add the ordered
      // one", so together they are what the swap is worth.
      costImpact: addCents(m.costImpact, e.costImpact),
    });
  }

  return discrepancies.flatMap((d) => {
    const swap = swaps.get(d);
    if (swap) return [swap];
    return used.has(d) ? [] : [d];
  });
}

/**
 * An area's material lines, generated as the estimate builder generated them:
 * its own template on its own roof, with the estimate's answers, priced from
 * its own supplier. A line that names no supplier of its own is the area's
 * supplier's, so it is expected on that supplier's order.
 */
function areaLines(
  area: GenerationArea,
  input: QuoteCheckInput,
  answers: TemplateAnswers,
): GeneratedLine[] {
  const lines = linesToPlan(
    area.template,
    area.templateItems,
    input.catalogue,
    area.roofs,
    answers,
  );
  const plan = generatePlan(
    lines.items,
    lines.roofs,
    input.catalogue,
    input.prices,
    area.supplierId,
    area.template.roof_type === "Shingle" ? { installOrder: "shingle" } : {},
  );
  return plan.materialItems.map((line) => ({
    ...line,
    supplierId: line.supplierId ?? area.supplierId,
  }));
}

/**
 * Compare a material order with the parts its estimate implies.
 *
 * Runs `linesToPlan` then `generatePlan` exactly as the estimate builder does,
 * keeps the material lines that belong on this order, adds the change orders,
 * then pairs every expected line with the order's lines — by product first,
 * then by name for a line typed on without one.
 *
 * An order is checked together with the estimate's other orders of the same
 * kind (`otherOrders`): a rep who splits one supplier's parts across two
 * deliveries has ordered the quote once, not half of it twice. A part on a
 * sibling order is not missing from this one; a difference in how much was
 * ordered is reported on the orders that carry the part; a line nothing quoted
 * is reported on the order it is on.
 */
export function compareOrderToQuote(input: QuoteCheckInput): QuoteCheckResult {
  const { estimate, order } = input;
  const templateId = estimate.roof_template_id ?? null;

  if (!templateId || !input.template) {
    return { status: "cannot_compare", reason: "no_template" };
  }
  if (!estimate.generation_answers) {
    return { status: "cannot_compare", reason: "no_answers" };
  }
  // The estimate's own recipes: its package and each of its areas.
  const ownTemplates = new Set([
    templateId,
    ...(input.areas ?? []).map((area) => area.templateId),
  ]);
  const orderTemplateId = order.roof_template_id ?? null;
  if (orderTemplateId !== null && !ownTemplates.has(orderTemplateId)) {
    return {
      status: "cannot_compare",
      reason: "different_template",
      estimateTemplateId: templateId,
      orderTemplateId,
    };
  }

  const generationSupplierId = estimate.generation_supplier_id ?? null;
  const headline = headlineOf(input.estimateItems ?? []);

  // Squares first: the answers decide which lines exist, and a resized roof
  // must go through the same rule the estimate builder ran.
  const resized = headline
    ? resizedToSold(input.measurements, num(headline.qty))
    : null;
  const lines = linesToPlan(
    input.template,
    input.templateItems,
    input.catalogue,
    resized ? resized.roofs : input.measurements,
    estimate.generation_answers,
  );

  // Then the product: the estimate sells Timberline NS where the template
  // produced HDZ, so the quote is NS, and every line of HDZ becomes NS.
  const soldProductId = headline?.product_id ?? null;
  const templateProductId = lines.headlineProductId;
  const productSwapped =
    soldProductId !== null &&
    templateProductId !== null &&
    soldProductId !== templateProductId &&
    input.catalogue.some((p) => p.id === soldProductId);
  const soldItems = productSwapped
    ? lines.items.map((item) =>
        item.product_id === templateProductId
          ? { ...item, product_id: soldProductId }
          : item,
      )
    : lines.items;

  const sold: SoldAdjustments | null =
    productSwapped || resized
      ? {
          product: productSwapped
            ? {
                templateProductId: templateProductId!,
                soldProductId: soldProductId!,
              }
            : null,
          squares: resized
            ? { measured: resized.measured, sold: num(headline!.qty) }
            : null,
        }
      : null;

  const plan = generatePlan(
    soldItems,
    lines.roofs,
    input.catalogue,
    input.prices,
    generationSupplierId,
    input.template.roof_type === "Shingle" ? { installOrder: "shingle" } : {},
  );

  // Every part the estimate buys: the main package's, then each area's, the
  // area's lines carrying its supplier so they land with that supplier's
  // order. An area bought from the main supplier is on the main order.
  const materialItems = [
    ...plan.materialItems,
    ...(input.areas ?? []).flatMap((area) =>
      areaLines(area, input, estimate.generation_answers!),
    ),
  ];

  const partition = partitionOf(materialItems, order, generationSupplierId);
  const expected = withChangeOrders(
    linesForPartition(materialItems, partition, generationSupplierId),
    input.changeOrders ?? [],
    input.catalogue,
    input.prices,
    generationSupplierId,
    materialItems,
    partition,
  );

  // The estimate's other orders of the same kind, whose lines count too.
  const siblings = (input.otherOrders ?? []).filter(
    (other) =>
      (other.roof_template_id ?? null) === null ||
      ownTemplates.has(other.roof_template_id!),
  );
  const pooled = siblings.filter(
    (other) =>
      partitionOf(materialItems, other, generationSupplierId) === partition,
  );
  const pool: OrderedLine[] = [
    ...order.items,
    ...pooled.flatMap((other) => other.items),
  ];
  const comparePrices = (order.status ?? "not_placed") === "not_placed";
  const own = new Set(order.items.map((item) => item.id));

  /*
   * Pair each order line with an expected one. Product first across the whole
   * order, then a proposed pairing for a typed line, then names for what is
   * left, so a typed line cannot take a pairing a line carrying the product
   * would have made.
   */
  const pairedTo = new Map<string, number>();
  pool.forEach((item) => {
    if (!item.product_id) return;
    const at = expected.findIndex(
      (line) => line.product_id === item.product_id,
    );
    if (at >= 0) pairedTo.set(item.id, at);
  });
  const proposedPairings: QuotePairing[] = [];
  for (const pairing of input.pairings ?? []) {
    const item = order.items.find((i) => i.id === pairing.orderItemId);
    if (!item || item.product_id || pairedTo.has(item.id)) continue;
    const at = expected.findIndex(
      (line) => line.product_id === pairing.productId,
    );
    if (at < 0) continue;
    pairedTo.set(item.id, at);
    proposedPairings.push(pairing);
  }
  pool.forEach((item) => {
    if (pairedTo.has(item.id)) return;
    const name = foldName(item.name ?? item.description);
    if (name === "") return;
    const at = expected.findIndex(
      (line) => foldName(line.description) === name,
    );
    if (at >= 0) pairedTo.set(item.id, at);
  });

  const discrepancies: QuoteDiscrepancy[] = [];
  let matched = 0;
  const productById = new Map(input.catalogue.map((p) => [p.id, p]));

  expected.forEach((line, at) => {
    const ordered = pool.filter((item) => pairedTo.get(item.id) === at);
    if (ordered.length === 0) {
      discrepancies.push({
        kind: "not_ordered",
        description: line.description,
        productId: line.product_id,
        itemId: null,
        quotedQty: line.qty,
        orderedQty: null,
        quotedPrice: line.unitPrice,
        orderedPrice: null,
        quotedSquares: squaresCovered(
          productById.get(line.product_id),
          line.qty,
        ),
        orderedSquares: null,
        costImpact: subCents(
          0 as Cents,
          multiplyCents(line.unitPrice, line.qty),
        ),
      });
      return;
    }

    matched += 1;
    const here = ordered.filter((item) => own.has(item.id));
    // Only on a sibling order: that order's check reports it.
    if (here.length === 0) return;

    const orderedQty = ordered.reduce((sum, item) => sum + num(item.qty), 0);
    const elsewhere = ordered
      .filter((item) => !own.has(item.id))
      .reduce((sum, item) => sum + num(item.qty), 0);
    // Quantity is pooled, but each line keeps its own price. No single line
    // can correct the pooled quantity; each price can be corrected separately.
    const orderedPrice = toCents(here[0]!.unit_price);
    const itemId = ordered.length === 1 ? here[0]!.id : null;
    const common = {
      description: line.description,
      productId: line.product_id,
      itemId,
      quotedQty: line.qty,
      orderedQty,
      quotedPrice: line.unitPrice,
      orderedPrice,
      quotedSquares: squaresCovered(productById.get(line.product_id), line.qty),
      orderedSquares: squaresCovered(
        productById.get(line.product_id),
        orderedQty,
      ),
      ...(elsewhere > 0 ? { orderedElsewhere: elsewhere } : {}),
    };

    if (Math.abs(orderedQty - line.qty) > QTY_TOLERANCE) {
      discrepancies.push({
        ...common,
        kind: "qty",
        // With several lines, price differences below are costed at each
        // actual quantity. Cost the quantity difference at the quoted price
        // so the two kinds still add up without counting any overrun twice.
        costImpact: multiplyCents(
          ordered.length === 1 ? orderedPrice : line.unitPrice,
          orderedQty - line.qty,
        ),
      });
    }
    for (const item of comparePrices ? here : []) {
      const price = toCents(item.unit_price);
      if (price === line.unitPrice) continue;
      const priceQty = ordered.length === 1 ? line.qty : num(item.qty);
      discrepancies.push({
        ...common,
        kind: "price",
        itemId: item.id,
        quotedQty: priceQty,
        orderedQty: num(item.qty),
        orderedPrice: price,
        quotedSquares: squaresCovered(
          productById.get(line.product_id),
          priceQty,
        ),
        orderedSquares: squaresCovered(
          productById.get(line.product_id),
          num(item.qty),
        ),
        // A single line retains the quoted-quantity decomposition. Split
        // lines use their own quantities, including discounts and overcharges.
        costImpact: multiplyCents(subCents(price, line.unitPrice), priceQty),
      });
    }
  });

  for (const item of order.items) {
    if (pairedTo.has(item.id)) continue;
    const qty = num(item.qty);
    const price = toCents(item.unit_price);
    discrepancies.push({
      kind: "not_quoted",
      description: item.name ?? item.description ?? "(unnamed line)",
      productId: item.product_id ?? null,
      itemId: item.id,
      quotedQty: null,
      orderedQty: qty,
      quotedPrice: null,
      orderedPrice: price,
      quotedSquares: null,
      orderedSquares: item.product_id
        ? squaresCovered(productById.get(item.product_id), qty)
        : null,
      costImpact: multiplyCents(price, qty),
    });
  }

  const quotedTotal = sumBy(expected, (line) =>
    multiplyCents(line.unitPrice, line.qty),
  );
  const orderedTotal = sumBy(pool, (item) =>
    multiplyCents(toCents(item.unit_price), num(item.qty)),
  );

  return {
    status: "compared",
    expected,
    discrepancies: withSwaps(discrepancies, input.catalogue),
    matched,
    proposedPairings,
    sold,
    checkedWith: pooled
      .map((other) => other.id)
      .filter((id): id is string => Boolean(id)),
    quotedTotal,
    orderedTotal,
    netImpact: subCents(orderedTotal, quotedTotal),
  };
}

/**
 * A fingerprint of the differences somebody looked at.
 *
 * Stored on the order when a person has dealt with every difference, and
 * compared on every open. Any change to what differs — a new line, another
 * bundle, a price that moved — changes it, so the order reads as unreviewed
 * again rather than carrying an old approval over a new problem.
 */
/**
 * One difference, with the numbers that make it a difference.
 *
 * What a Keep is recorded against, and what `quoteReviewSignature` is built
 * from. A keep of six boxes against nine says nothing about six against
 * thirty, so a keep follows the numbers rather than the line: change the line
 * and its difference asks to be looked at again.
 */
export function quoteDiscrepancyFingerprint(d: QuoteDiscrepancy): string {
  return [
    d.kind,
    d.productId ?? "",
    d.orderedProductId ?? "",
    d.itemId ?? "",
    d.quotedQty ?? "",
    d.orderedQty ?? "",
    d.quotedPrice ?? "",
    d.orderedPrice ?? "",
  ].join(":");
}

export function quoteReviewSignature(
  discrepancies: readonly QuoteDiscrepancy[],
): string {
  return discrepancies.map(quoteDiscrepancyFingerprint).sort().join("|");
}

/** What the banner on a material order says. */
export type QuoteCheckState =
  /** Nothing to compare against. Said, never passed. */
  | "cannot_compare"
  /** No differences at all. */
  | "matches"
  /** Differences nobody has dealt with. Placing the order warns. */
  | "unreviewed"
  /** Differences a person has looked at and kept. */
  | "reviewed";

export function quoteCheckState(
  result: QuoteCheckResult,
  reviewedSignature: string | null | undefined,
): QuoteCheckState {
  if (result.status === "cannot_compare") return "cannot_compare";
  if (result.discrepancies.length === 0) return "matches";
  return reviewedSignature === quoteReviewSignature(result.discrepancies)
    ? "reviewed"
    : "unreviewed";
}

/**
 * A key for one difference, stable across re-reads of the same order and
 * across edits to its numbers. For identifying a row on screen only: what a
 * person kept is recorded by `quoteDiscrepancyFingerprint`.
 */
export function quoteDiscrepancyKey(d: QuoteDiscrepancy): string {
  return [d.kind, d.productId ?? "", d.itemId ?? ""].join(":");
}

/**
 * Whether a material order is checked against its quote at all.
 *
 * Jonathon, 2026-09-21: "let's turn this feature off for now." The check works
 * — what it did not survive is a real order, where fourteen rows of difference
 * were mostly parts deliberately not bought from that supplier, and a panel
 * that cries wolf twelve times out of fourteen is one people learn to scroll
 * past on the way to the lines they came to edit.
 *
 * Hidden rather than deleted (ADR 0190), the same as financing (ADR 0117) and
 * the partner network (ADR 0144): every table, column and screen stays, and
 * one row decides whether any of it runs.
 *
 * Unknown reads as off, as it does for the partner network and unlike
 * `showFinancing`. A panel that appears for a moment while the settings load
 * and is then taken away is worse than one that arrives a moment late — and it
 * makes a database the migration has not reached yet read as off, which is the
 * answer that was wanted anyway.
 */
export function quoteCheckEnabled(
  /** `company_settings.quote_check_enabled`. */
  setting: boolean | null | undefined,
): boolean {
  return setting === true;
}
